import { tourMatchesPathname } from '../routing/utils'
import {
  isGuideTourFeatureFlagOn,
  resolveGuideTourFeatureFlagSignal,
} from './featureFlagRuntime'
import type { GuideTourDefinition, GuideTourStep } from '../types'

const getGuideTourCurrentPathname = (): string =>
  typeof window !== 'undefined' ? window.location.pathname : '/'

const isGuideTourFeatureFlagRouteActive = (
  tour: Pick<GuideTourDefinition, 'steps' | 'route'>
): boolean =>
  tourMatchesPathname(
    tour as GuideTourDefinition,
    getGuideTourCurrentPathname()
  )

export const resolveGuideTourFeatureFlag = (
  tour: Pick<GuideTourDefinition, 'featureFlag'>,
  step: Pick<GuideTourStep, 'featureFlag'>
): string | undefined => step.featureFlag ?? tour.featureFlag

export const isGuideTourStepAllowedByFeatureFlag = (
  tour: GuideTourDefinition,
  step: GuideTourStep
): boolean => {
  const flag = resolveGuideTourFeatureFlag(tour, step)

  if (!flag) {
    return true
  }

  if (!isGuideTourFeatureFlagRouteActive(tour)) {
    return false
  }

  return isGuideTourFeatureFlagOn(flag)
}

const walkGuideTourSteps = (steps: readonly GuideTourStep[]): GuideTourStep[] =>
  steps.flatMap(step => [
    step,
    ...(step.whenTargetMissing?.flatMap(flow =>
      walkGuideTourSteps(flow.steps)
    ) ?? []),
  ])

export const hasGuideTourFeatureFlagPending = (
  tour: Pick<GuideTourDefinition, 'featureFlag' | 'steps' | 'route'>
): boolean => {
  if (!isGuideTourFeatureFlagRouteActive(tour)) {
    return false
  }

  for (const step of walkGuideTourSteps(tour.steps)) {
    const flag = resolveGuideTourFeatureFlag(tour, step)
    if (flag && resolveGuideTourFeatureFlagSignal(flag) === 'pending') {
      return true
    }
  }

  if (
    tour.featureFlag &&
    resolveGuideTourFeatureFlagSignal(tour.featureFlag) === 'pending'
  ) {
    return true
  }

  return false
}

export const tourUsesGuideTourFeatureFlag = (
  tour: Pick<GuideTourDefinition, 'featureFlag' | 'steps'>
): boolean => collectGuideTourFeatureFlagNames([tour]).length > 0

export const collectGuideTourFeatureFlagNames = (
  definitions: readonly Pick<GuideTourDefinition, 'featureFlag' | 'steps'>[]
): string[] => {
  const names = new Set<string>()

  for (const tour of definitions) {
    if (tour.featureFlag) {
      names.add(tour.featureFlag)
    }

    for (const step of walkGuideTourSteps(tour.steps)) {
      if (step.featureFlag) {
        names.add(step.featureFlag)
      }
    }
  }

  return Array.from(names).sort((a, b) => a.localeCompare(b))
}
